import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { ArrowLeft, Pencil, X } from 'lucide-react'
import { DateTime } from 'luxon'
import { useForm, useWatch, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import { useSaveBillingData } from '@/core/hooks/account/useSaveBillingData'
import { useLanguages } from '@/core/hooks/misc/useLanguages'
import {
	useAlegraCities,
	useAlegraCoCities,
	useAlegraCoDepartments,
	useAlegraCoZipCodes,
	useAlegraCommunes,
	useAlegraEsCities,
	useAlegraEsProvinces,
	useAlegraProvinces,
	useAlegraRegions,
} from '@/core/hooks/payment/useAlegraLocations'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { BillingInformationFormData } from './BillingInformation.types'
import { BillingInformationUI } from './BillingInformationUI'

const normalize = (value: unknown): string => {
	if (typeof value !== 'string') return ''
	return value.trim()
}

// Compare two values for equality (handles arrays and strings)
const areValuesEqual = (a: unknown, b: unknown): boolean => {
	// Handle arrays (like notification_emails)
	if (Array.isArray(a) && Array.isArray(b)) {
		const filteredA = a.filter((v) => normalize(v) !== '').map(normalize)
		const filteredB = b.filter((v) => normalize(v) !== '').map(normalize)
		if (filteredA.length !== filteredB.length) return false
		return filteredA.every((val, idx) => val === filteredB[idx])
	}

	// Handle strings and other values
	const normalizedA = normalize(a)
	const normalizedB = normalize(b)
	return normalizedA === normalizedB
}

// Check if form data has changed from the original values
const hasFormDataChanged = (
	currentData: BillingInformationFormData,
	originalData: BillingInformationFormData
): boolean => {
	const keys = Object.keys(currentData) as (keyof BillingInformationFormData)[]
	return keys.some(
		(key) => !areValuesEqual(currentData[key], originalData[key])
	)
}

const normalizeUpperNoSpaces = (value: unknown): string => {
	return normalize(value).toUpperCase().replace(/\s+/g, '')
}

const ALEGRA_ISO = {
	USA: normalizeUpperNoSpaces('Alegra USA'),
	MEX: normalizeUpperNoSpaces('Alegra MEX'),
	ARG: normalizeUpperNoSpaces('Alegra ARG'),
	CHL: normalizeUpperNoSpaces('Alegra CHL'),
	ESP: normalizeUpperNoSpaces('Alegra ESP'),
	COL: normalizeUpperNoSpaces('Alegra COL'),
} as const

const isValidEmailOrEmpty = (value: unknown): boolean => {
	const v = normalize(value)
	if (v === '') return true
	// yup's email() allows empty unless required; we want "empty OR valid email"
	return yup.string().email().isValidSync(v)
}

interface BillingInformationProps {
	onBack: () => void
	hideHeader?: boolean
	// Form ID for external submission
	formId?: string
	// Hide action buttons (save/cancel)
	hideActionButtons?: boolean
	// Callback on successful save
	onSuccess?: () => void
	// Optional initial values to hydrate drafts (e.g., from stepper navigation)
	initialValues?: Partial<BillingInformationFormData>
	// Notify parent of form draft changes
	onDraftChange?: (data: BillingInformationFormData) => void
	// Notify parent when saving state changes (useful for external submit buttons)
	onSavingChange?: (isSaving: boolean) => void
}

const BillingInformation: React.FC<BillingInformationProps> = ({
	onBack,
	hideHeader = false,
	formId,
	hideActionButtons = false,
	onSuccess,
	initialValues,
	onDraftChange,
	onSavingChange,
}) => {
	const { t } = useTranslation('common')
	const [searchParams, setSearchParams] = useSearchParams()
	const { showNotification } = useNotificationContext()
	const account = useCurrentAccount()
	const [isMobile, setIsMobile] = useState(() => {
		if (typeof window === 'undefined') return false
		return window.matchMedia('(max-width: 767px)').matches
	})
	const [isMobileEditing, setIsMobileEditing] = useState(false)
	const isBillingMobileEditAction =
		searchParams.get('action') === 'edit-billing'

	const clearBillingMobileEditAction = useCallback(() => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev)
			if (next.get('action') === 'edit-billing') {
				next.delete('action')
			}
			return next
		})
	}, [setSearchParams])

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 767px)')
		const onChange = () => setIsMobile(mq.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])

	useEffect(() => {
		if (isMobile && hideHeader) {
			setIsMobileEditing(true)
		} else if (isMobile && isBillingMobileEditAction) {
			setIsMobileEditing(true)
		}
	}, [isMobile, hideHeader, isBillingMobileEditAction])

	// Use the centralized refetch hook
	const refetchCurrentAccountData = useRefetchAccountData()

	const { data: languages = [] } = useLanguages()

	// Memoize computed values
	const accountId = useMemo(() => account?.id || 0, [account?.id])
	const billingData = useMemo(
		() => account?.billingInfo?.billingData,
		[account?.billingInfo?.billingData]
	)
	const companyDetails = useMemo(
		() => account?.billingInfo?.companyDetails,
		[account?.billingInfo?.companyDetails]
	)
	const billingSystem = useMemo(
		() => account?.billingInfo?.billingSystem,
		[account?.billingInfo?.billingSystem]
	)
	const subscriptions = useMemo(
		() => account?.subscriptions || [],
		[account?.subscriptions]
	)

	// Edit mode: if billing_data exists with an ID, we're in edit mode
	const isEditMode = useMemo(() => {
		return !!billingData?.id
	}, [billingData?.id])

	// Store the billing data loaded timestamp for submission
	// This captures the moment when billing data was loaded, not when form is submitted
	const billingDataLoadedAtRef = useRef<string | null>(null)

	// Store initial values on mount to prevent re-initializing on every render
	const initialValuesRef = useRef(initialValues)

	// Capture the timestamp when billing data first loads
	useEffect(() => {
		if (billingData && !billingDataLoadedAtRef.current) {
			billingDataLoadedAtRef.current = DateTime.now().toFormat(
				"yyyy-MM-dd'T'HH:mm:ssZZ"
			)
		}
	}, [billingData])

	const saveBillingDataMutation = useSaveBillingData({
		accountId,
		onSuccess: () => {
			// Refresh current account data in Redux (selected account if set, otherwise user)
			void refetchCurrentAccountData()
			showNotification({
				type: 'success',
				message: t('account.billing_information.save_success'),
				description: t('account.billing_information.save_success_description'),
			})
			if (isMobile) {
				setIsMobileEditing(false)
				clearBillingMobileEditAction()
			}
			if (onSuccess) {
				onSuccess()
			}
		},
		onError: (error: any) => {
			showNotification({
				type: 'error',
				message: t('account.billing_information.save_error'),
				description:
					extractErrorMessage(error) ||
					t('account.billing_information.save_error_description'),
			})
		},
	})

	// Allow parent to reflect saving state (e.g., /subscribe Summary Continue button)
	useEffect(() => {
		onSavingChange?.(saveBillingDataMutation.isPending)
	}, [onSavingChange, saveBillingDataMutation.isPending])

	// Get regime values - will be updated when form is initialized
	const [kindOfPerson, setKindOfPerson] = useState<string | undefined>()

	// Country/region behavior should be driven by Billing's `alegra_iso` (not the form's `country` field).
	// For brand new accounts `alegraIso` may be empty; default to Alegra USA as requested.
	const alegraIso = useMemo(() => {
		const iso = account?.billingInfo?.alegraIso
		return iso && iso.trim() ? iso.trim() : 'Alegra USA'
	}, [account?.billingInfo?.alegraIso])
	const alegraIsoNormalized = useMemo(
		() => normalizeUpperNoSpaces(alegraIso),
		[alegraIso]
	)

	const hasMxValidations = alegraIsoNormalized === ALEGRA_ISO.MEX
	const hasArValidations = alegraIsoNormalized === ALEGRA_ISO.ARG
	const hasClValidations = alegraIsoNormalized === ALEGRA_ISO.CHL
	const hasEsValidations = alegraIsoNormalized === ALEGRA_ISO.ESP
	const hasCoValidations = alegraIsoNormalized === ALEGRA_ISO.COL

	// Location hooks based on country - will be initialized after form
	// CHL
	const { data: chlRegions = [] } = useAlegraRegions({
		enabled: hasClValidations,
	})

	// ARG
	const { data: argProvinces = [] } = useAlegraProvinces({
		enabled: hasArValidations,
	})

	// ESP
	const { data: espProvinces = [] } = useAlegraEsProvinces({
		enabled: hasEsValidations,
	})

	// COL
	const { data: colDepartments = [] } = useAlegraCoDepartments({
		enabled: hasCoValidations,
	})

	// Validation schema
	const schema = useMemo(
		() =>
			yup.object().shape({
				notification_emails: yup
					.array()
					.of(
						yup
							.string()
							.transform((v) => normalize(v))
							.test(
								'email-or-empty',
								t('validation.email'),
								isValidEmailOrEmpty
							)
					)
					.test('at-least-one-email', t('validation.required'), (emails) => {
						return !!emails?.some((e) => normalize(e) !== '')
					})
					.required(t('validation.required')),
				country: yup.string().required(t('validation.required')),
				state: yup.string().required(t('validation.required')),
				city: yup.string().required(t('validation.required')),
				street: yup.string().required(t('validation.required')),
				zip: hasMxValidations
					? yup
							.string()
							.transform((v) => normalize(v))
							.required(t('validation.required'))
							.min(5, t('validation.minLength', { min: 5 }))
							.max(5, t('validation.maxLength', { max: 5 }))
					: hasCoValidations
						? yup
								.string()
								.transform((v) => normalize(v))
								.required(t('validation.required'))
								.min(6, t('validation.minLength', { min: 6 }))
								.max(6, t('validation.maxLength', { max: 6 }))
						: yup
								.string()
								.transform((v) => normalize(v))
								.required(t('validation.required'))
								.max(10, t('validation.maxLength', { max: 10 })),
				official_company_name: yup.string().required(t('validation.required')),
				tax: yup
					.string()
					.transform((v) => normalize(v))
					.required(t('validation.required'))
					.matches(/^[a-zA-Z0-9ñÑ&]+$/i, t('validation.invalid'))
					.min(5, t('validation.minLength', { min: 5 }))
					.max(20, t('validation.maxLength', { max: 20 })),
				billing_contact_full_name: yup
					.string()
					.required(t('validation.required')),
				invoice_language: yup.string().optional(),
				contact_phone: yup.string().optional(),
				contact_name: yup.string().optional(),
				// Alegra MEX validations
				municipality: hasMxValidations
					? yup
							.string()
							.required(t('validation.required'))
							.max(500, t('validation.max_length', { max: 500 }))
					: yup.string().optional(),
				ext_number: hasMxValidations
					? yup.string().max(10, t('validation.max_length', { max: 10 }))
					: yup.string().optional(),
				int_number: hasMxValidations
					? yup.string().max(10, t('validation.max_length', { max: 10 }))
					: yup.string().optional(),
				regime: hasMxValidations
					? yup.string().optional()
					: yup.string().optional(),
				// Alegra ARG validations
				alegra_identification_type_arg: hasArValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				alegra_iva_condition: hasArValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				// Alegra CHL validations
				alegra_region: hasClValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				alegra_commune: hasClValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				// Alegra ESP validations
				alegra_identification_type_esp: hasEsValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				// Alegra COL validations
				alegra_identification_type_col: hasCoValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				alegra_identification_verification_digit: hasCoValidations
					? yup
							.string()
							.required(t('validation.required'))
							.matches(/^[0-9]$/, 'Must be a number between 0 and 9')
					: yup.string().optional(),
				alegra_kind_of_person: hasCoValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				alegra_regime: hasCoValidations
					? yup.string().required(t('validation.required'))
					: yup.string().optional(),
				alegra_firstname: hasCoValidations
					? yup
							.string()
							.required(t('validation.required'))
							.max(40, t('validation.max_length', { max: 40 }))
					: yup.string().optional(),
				alegra_lastname: hasCoValidations
					? yup
							.string()
							.required(t('validation.required'))
							.max(40, t('validation.max_length', { max: 40 }))
					: yup.string().optional(),
			}),
		[
			t,
			hasMxValidations,
			hasArValidations,
			hasClValidations,
			hasEsValidations,
			hasCoValidations,
		]
	)

	// Memoize form default values
	const formDefaultValues = useMemo<BillingInformationFormData>(() => {
		const base: BillingInformationFormData = {
			notification_emails:
				billingData?.notificationEmails &&
				billingData.notificationEmails.length > 0
					? billingData.notificationEmails
					: [
							billingData?.notificationEmails?.[0] ||
								companyDetails?.email ||
								'',
						],
			country: billingData?.country || '',
			state: billingData?.state || '',
			city: billingData?.city || '',
			street: billingData?.street || '',
			zip: billingData?.zip || '',
			official_company_name: billingData?.officialCompanyName || '',
			tax: billingData?.tax || companyDetails?.tax || '',
			billing_contact_full_name:
				billingData?.custom?.contactName || companyDetails?.name || '',
			invoice_language: billingData?.custom?.invoiceLanguage || '',
			contact_phone: billingData?.custom?.contactPhone || '',
			contact_name: billingData?.custom?.contactName || '',
			// Alegra MEX fields
			municipality: billingData?.custom?.municipality || '',
			ext_number: billingData?.custom?.extNumber || '',
			int_number: billingData?.custom?.intNumber || '',
			regime:
				billingData?.custom?.regime ||
				(hasMxValidations ? 'GENERAL_REGIME_OF_MORAL_PEOPLE_LAW' : ''),
			// Alegra ARG fields
			alegra_identification_type_arg:
				billingData?.custom?.alegraIdentificationType || '',
			alegra_iva_condition: billingData?.custom?.alegraIvaCondition || '',
			// Alegra CHL fields
			alegra_region: billingData?.custom?.alegraRegion || '',
			alegra_commune: billingData?.custom?.alegraCommune || '',
			// Alegra ESP fields
			alegra_identification_type_esp:
				billingData?.custom?.alegraIdentificationType || '',
			// Alegra COL fields
			alegra_identification_type_col:
				billingData?.custom?.alegraIdentificationType || '',
			alegra_identification_verification_digit:
				billingData?.custom?.alegraIdentificationVerificationDigit || '',
			alegra_kind_of_person: billingData?.custom?.alegraKindOfPerson || '',
			alegra_regime: billingData?.custom?.alegraRegime || '',
			alegra_firstname: billingData?.custom?.alegraFirstname || '',
			alegra_lastname: billingData?.custom?.alegraLastname || '',
		}

		// Merge any incoming draft values on top of the account defaults
		// Use ref to prevent re-initialization on every render
		const storedInitialValues = initialValuesRef.current
		if (!storedInitialValues) return base

		return {
			...base,
			...storedInitialValues,
		}
	}, [
		companyDetails?.email,
		billingData?.country,
		billingData?.state,
		billingData?.city,
		billingData?.street,
		billingData?.zip,
		billingData?.officialCompanyName,
		billingData?.tax,
		companyDetails?.tax,
		billingData?.custom?.contactName,
		companyDetails?.name,
		billingData?.custom?.invoiceLanguage,
		billingData?.custom?.contactPhone,
		billingData?.custom?.municipality,
		billingData?.custom?.extNumber,
		billingData?.custom?.intNumber,
		billingData?.custom?.regime,
		billingData?.custom?.alegraIdentificationType,
		billingData?.custom?.alegraIvaCondition,
		billingData?.custom?.alegraRegion,
		billingData?.custom?.alegraCommune,
		billingData?.custom?.alegraIdentificationVerificationDigit,
		billingData?.custom?.alegraKindOfPerson,
		billingData?.custom?.alegraRegime,
		billingData?.custom?.alegraFirstname,
		billingData?.custom?.alegraLastname,
		hasMxValidations,
	])

	const form = useForm<BillingInformationFormData>({
		resolver: yupResolver(schema) as any,
		defaultValues: formDefaultValues,
	})

	// Watch form values for location dependencies
	const selectedChlRegion = useWatch({ control: form.control, name: 'state' })
	const selectedArgProvince = useWatch({ control: form.control, name: 'state' })
	const selectedEspProvince = useWatch({ control: form.control, name: 'state' })
	const selectedColDepartment = useWatch({
		control: form.control,
		name: 'state',
	})
	const selectedColCity = useWatch({ control: form.control, name: 'city' })

	// Location hooks that depend on form values
	const { data: chlCommunes = [], isLoading: isLoadingChlCommunes } =
		useAlegraCommunes(selectedChlRegion, {
			enabled: hasClValidations && !!selectedChlRegion,
		})
	// For Argentina, selectedArgProvince should be the ID (since SelectItem uses ID as value)
	// Convert to number if it's a string
	const selectedArgProvinceId = useMemo(() => {
		if (!selectedArgProvince || !hasArValidations) return undefined
		const id =
			typeof selectedArgProvince === 'number'
				? selectedArgProvince
				: Number(selectedArgProvince)
		return isNaN(id) ? undefined : id
	}, [selectedArgProvince, hasArValidations])
	const { data: argCities = [], isLoading: isLoadingArgCities } =
		useAlegraCities(selectedArgProvinceId, {
			enabled: hasArValidations && !!selectedArgProvinceId,
		})
	// For Spain, the form stores province name (value) since backend returns names
	// We need to look up the province ID from the name to fetch cities
	const selectedEspProvinceId = useMemo(() => {
		if (!selectedEspProvince || !hasEsValidations || espProvinces.length === 0)
			return undefined
		const province = espProvinces.find((p) => p.value === selectedEspProvince)
		return province ? Number(province.id) : undefined
	}, [selectedEspProvince, hasEsValidations, espProvinces])
	const { data: espCities = [], isLoading: isLoadingEspCities } =
		useAlegraEsCities(selectedEspProvinceId, {
			enabled: hasEsValidations && !!selectedEspProvinceId,
		})
	// For Colombia, selectedColDepartment should be the ID (since SelectItem uses ID as value)
	// Convert to number if it's a string
	const selectedColDepartmentId = useMemo(() => {
		if (!selectedColDepartment || !hasCoValidations) return undefined
		const id =
			typeof selectedColDepartment === 'number'
				? selectedColDepartment
				: Number(selectedColDepartment)
		return isNaN(id) ? undefined : id
	}, [selectedColDepartment, hasCoValidations])
	const { data: colCities = [], isLoading: isLoadingColCities } =
		useAlegraCoCities(selectedColDepartmentId, {
			enabled: hasCoValidations && !!selectedColDepartmentId,
		})
	// Fetch all ZIP codes once for Colombia (backend doesn't filter)
	const { data: allColZipCodes = [], isLoading: isLoadingColZipCodes } =
		useAlegraCoZipCodes({
			enabled: hasCoValidations,
		})
	// Filter ZIP codes by selected city on the frontend
	// ZIP codes have cityId field that matches the city's id
	const colZipCodes = useMemo(() => {
		if (
			!selectedColCity ||
			allColZipCodes.length === 0 ||
			colCities.length === 0
		)
			return []
		// Find the city ID from the selected city name
		const selectedCityObj = colCities.find(
			(city) => city.value === selectedColCity
		)
		if (!selectedCityObj) return []
		// Filter ZIP codes by matching cityId
		return (
			allColZipCodes
				//.filter(zip => String(zip.cityId) === String(selectedCityObj.id)) // TODO: Uncomment this after Alegra assertions
				.map((zip) => ({ id: zip.value, value: zip.value }))
		) // Map to AlegraLocationOption format for UI
	}, [selectedColCity, allColZipCodes, colCities])

	// Update form when account data loads
	useEffect(() => {
		if (
			formDefaultValues &&
			Object.values(formDefaultValues).some((val) => val !== '')
		) {
			form.reset(formDefaultValues)
			// Set initial kind of person for regime values
			if (formDefaultValues.alegra_kind_of_person) {
				setKindOfPerson(formDefaultValues.alegra_kind_of_person)
			}
		}
	}, [formDefaultValues, form])

	// Keep parent informed of draft changes to allow stepper persistence
	useEffect(() => {
		if (!onDraftChange) return
		const subscription = form.watch((value) => {
			onDraftChange(value as BillingInformationFormData)
		})
		return () => subscription.unsubscribe()
	}, [form, onDraftChange])

	const handleSubmit = (data: BillingInformationFormData) => {
		// If we're in edit mode and no changes were made, skip the API call and proceed
		if (isEditMode && !hasFormDataChanged(data, formDefaultValues)) {
			if (onSuccess) {
				onSuccess()
			}
			return
		}

		// Check if billing system is Alegra ESP, MEX, or USA
		const billingSystemName = billingSystem?.name || ''
		const isAlegraSystem =
			billingSystemName.includes('Alegra ESP') ||
			billingSystemName.includes('Alegra MEX') ||
			billingSystemName.includes('Alegra USA')

		// Build custom object
		const custom: any = {
			contact_name: data.contact_name || data.billing_contact_full_name,
			invoice_language: data.invoice_language || undefined,
			contact_phone: data.contact_phone || undefined,
		}

		// Add alegra_currency if billing system is Alegra
		if (isAlegraSystem) {
			// Try to get from existing billing_data.custom.alegra_currency
			let alegraCurrency = billingData?.custom?.alegraCurrency

			// If not found, get from active or in_trial subscription
			if (!alegraCurrency) {
				const activeSubscription = subscriptions.find(
					(sub: any) => sub.status === 'active' || sub.status === 'in_trial'
				)
				alegraCurrency = activeSubscription?.currency.toLowerCase()
			}

			if (alegraCurrency) {
				custom.alegra_currency = alegraCurrency
			}
		}

		// Default behavior: If no specific country validation is triggered,
		// or if specifically Alegra USA, default to credit-card.
		// This covers new accounts that default to USA.
		const isSpecificCountry =
			hasMxValidations ||
			hasArValidations ||
			hasClValidations ||
			hasEsValidations ||
			hasCoValidations

		if (!isSpecificCountry || billingSystemName.includes('Alegra USA')) {
			custom.alegra_payment_method = 'credit-card'
		}

		// Include fixed Alegra MEX fields
		if (hasMxValidations) {
			custom.municipality = data.municipality
			custom.alegra_payment_type = 'pue'
			custom.alegra_payment_method = 'transfer'
			custom.regime = data.regime
				? [data.regime]
				: ['GENERAL_REGIME_OF_MORAL_PEOPLE_LAW']
			if (data.ext_number) {
				custom.ext_number = data.ext_number
			}
			if (data.int_number) {
				custom.int_number = data.int_number
			}
		}

		// Include fixed Alegra ARG fields
		if (hasArValidations) {
			custom.alegra_identification_type = data.alegra_identification_type_arg
			custom.alegra_iva_condition = data.alegra_iva_condition
			custom.alegra_sale_concept = 'PRODUCTS'
			custom.alegra_sale_condition = 'TRANSFER'
		}

		// Include fixed Alegra CHL fields
		if (hasClValidations) {
			// For CHL, alegra_region uses state value, alegra_commune uses city value
			custom.alegra_region = data.state
			custom.alegra_commune = data.city
		}

		// Include fixed Alegra ESP fields
		if (hasEsValidations) {
			custom.alegra_identification_type = data.alegra_identification_type_esp
		}

		// Include fixed Alegra COL fields
		if (hasCoValidations) {
			custom.alegra_identification_type = data.alegra_identification_type_col
			custom.alegra_identification_verification_digit =
				data.alegra_identification_verification_digit
			custom.alegra_kind_of_person = data.alegra_kind_of_person
			custom.alegra_regime = data.alegra_regime
			custom.alegra_firstname = data.alegra_firstname
			custom.alegra_lastname = data.alegra_lastname
		}

		// Send billing_data fields to the new endpoint
		// Note: ESP (Spain) and other countries now store province/city names directly in the form,
		// which matches what the backend expects, so no conversion is needed
		const payload = {
			notification_emails: data.notification_emails.filter(
				(email) => email.trim() !== ''
			),
			country: data.country,
			state: data.state,
			city: data.city,
			street: data.street,
			zip: data.zip,
			official_company_name: data.official_company_name,
			tax: data.tax,
			custom,
			updated_at:
				billingDataLoadedAtRef.current ||
				DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"),
			bill_country: 'US',
		}

		saveBillingDataMutation.mutate(payload)
	}

	const handleCloseMobileEdit = () => {
		form.reset(formDefaultValues)
		setIsMobileEditing(false)
		clearBillingMobileEditAction()
	}

	const currentValues = form.getValues()
	const notificationEmails = (currentValues.notification_emails || []).filter(
		(email) => email?.trim() !== ''
	)
	const invoiceLanguageName =
		languages.find(
			(language) => language.ietf === currentValues.invoice_language
		)?.name || currentValues.invoice_language
	const mobileFormId = formId || 'billing-information-mobile-form'

	return (
		<FormProvider {...form}>
			{isMobile ? (
				<div className="w-full">
					{!hideHeader && (
						<div className="flex items-center justify-between mb-6">
							<div className="flex items-center gap-4">
								<Button
									variant="ghost"
									size="icon"
									onClick={onBack}
									className="h-8 w-8 cursor-pointer"
								>
									<ArrowLeft className="h-4 w-4" />
								</Button>
								<h1 className="text-2xl font-bold text-foreground">
									{t('account.billing_information.title')}
								</h1>
							</div>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => setIsMobileEditing(true)}
								className="w-11 h-11 rounded-full border border-border bg-card"
								aria-label={t('actions.edit')}
								data-testid="billing-edit-trigger"
							>
								<Pencil className="w-4 h-4" />
							</Button>
						</div>
					)}

					<div className="space-y-4 pb-24">
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.official_company_name')}
							</p>
							<p className="text-lg">
								{currentValues.official_company_name || '-'}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.company_tax_id')}
							</p>
							<p className="text-lg">{currentValues.tax || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.billing_contact_full_name')}
							</p>
							<p className="text-lg">
								{currentValues.billing_contact_full_name || '-'}
							</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.notification_emails')}
							</p>
							<p className="text-lg">
								{notificationEmails.length > 0
									? notificationEmails.join(', ')
									: '-'}
							</p>
						</div>
						<Separator />
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.street')}
							</p>
							<p className="text-lg">{currentValues.street || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.city')}
							</p>
							<p className="text-lg">{currentValues.city || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.state')}
							</p>
							<p className="text-lg">{currentValues.state || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.zip_code')}
							</p>
							<p className="text-lg">{currentValues.zip || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.country')}
							</p>
							<p className="text-lg">{currentValues.country || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.invoice_language')}
							</p>
							<p className="text-lg">{invoiceLanguageName || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.contact_phone')}
							</p>
							<p className="text-lg">{currentValues.contact_phone || '-'}</p>
						</div>
						<div>
							<p className="text-muted-foreground text-sm">
								{t('account.billing_information.contact_name')}
							</p>
							<p className="text-lg">{currentValues.contact_name || '-'}</p>
						</div>
					</div>

					<BottomSheet
						open={isMobileEditing}
						onOpenChange={(open) => {
							if (!open) {
								handleCloseMobileEdit()
								return
							}
							setIsMobileEditing(true)
						}}
					>
						<BottomSheetContent
							variant="card"
							className="border-border h-auto max-h-[95vh] px-0 pb-8"
							aria-describedby={undefined}
						>
							<div className="px-5 pt-2 space-y-6">
								<div className="flex items-center justify-between">
									<BottomSheetTitle className="text-xl md:text-2xl font-semibold">
										{t('account.billing_information.mobile.edit_title')}
									</BottomSheetTitle>
									<Button
										variant="ghost"
										size="icon"
										onClick={handleCloseMobileEdit}
										className="h-10 w-10 rounded-full"
										aria-label={t('actions.close')}
									>
										<X className="w-5 h-5" />
									</Button>
								</div>
								<Separator />

								<BillingInformationUI
									form={form}
									onBack={handleCloseMobileEdit}
									onSubmit={handleSubmit}
									isSaving={saveBillingDataMutation.isPending}
									languages={languages}
									hasMxValidations={hasMxValidations}
									hasArValidations={hasArValidations}
									hasClValidations={hasClValidations}
									hasEsValidations={hasEsValidations}
									hasCoValidations={hasCoValidations}
									chlRegions={chlRegions}
									chlCommunes={chlCommunes}
									argProvinces={argProvinces}
									argCities={argCities}
									espProvinces={espProvinces}
									espCities={espCities}
									colDepartments={colDepartments}
									colCities={colCities}
									colZipCodes={colZipCodes}
									isLoadingChlCommunes={isLoadingChlCommunes}
									isLoadingArgCities={isLoadingArgCities}
									isLoadingEspCities={isLoadingEspCities}
									isLoadingColCities={isLoadingColCities}
									isLoadingColZipCodes={isLoadingColZipCodes}
									selectedChlRegion={selectedChlRegion || ''}
									selectedArgProvince={selectedArgProvince || ''}
									selectedEspProvinceId={selectedEspProvinceId}
									selectedColDepartmentId={selectedColDepartmentId}
									selectedColCity={selectedColCity || ''}
									kindOfPerson={kindOfPerson}
									setKindOfPerson={setKindOfPerson}
									isEditMode={isEditMode}
									hideHeader
									formId={mobileFormId}
									hideActionButtons
								/>

								<Button
									type="submit"
									form={mobileFormId}
									className="w-full rounded-full"
									disabled={saveBillingDataMutation.isPending}
								>
									{saveBillingDataMutation.isPending
										? t('actions.saving')
										: t('actions.save')}
								</Button>
							</div>
						</BottomSheetContent>
					</BottomSheet>
				</div>
			) : (
				<BillingInformationUI
					form={form}
					onBack={onBack}
					onSubmit={handleSubmit}
					isSaving={saveBillingDataMutation.isPending}
					languages={languages}
					hasMxValidations={hasMxValidations}
					hasArValidations={hasArValidations}
					hasClValidations={hasClValidations}
					hasEsValidations={hasEsValidations}
					hasCoValidations={hasCoValidations}
					chlRegions={chlRegions}
					chlCommunes={chlCommunes}
					argProvinces={argProvinces}
					argCities={argCities}
					espProvinces={espProvinces}
					espCities={espCities}
					colDepartments={colDepartments}
					colCities={colCities}
					colZipCodes={colZipCodes}
					isLoadingChlCommunes={isLoadingChlCommunes}
					isLoadingArgCities={isLoadingArgCities}
					isLoadingEspCities={isLoadingEspCities}
					isLoadingColCities={isLoadingColCities}
					isLoadingColZipCodes={isLoadingColZipCodes}
					selectedChlRegion={selectedChlRegion || ''}
					selectedArgProvince={selectedArgProvince || ''}
					selectedEspProvinceId={selectedEspProvinceId}
					selectedColDepartmentId={selectedColDepartmentId}
					selectedColCity={selectedColCity || ''}
					kindOfPerson={kindOfPerson}
					setKindOfPerson={setKindOfPerson}
					isEditMode={isEditMode}
					hideHeader={hideHeader}
					formId={formId}
					hideActionButtons={hideActionButtons}
				/>
			)}
		</FormProvider>
	)
}

export default BillingInformation
